<template>
<div>
    <GoogleMap
        ref="mapRef"
        :api-key="googleApiKey"
        :api-promise="apiPromise"
        mapId="TripPlannerMap"
        :center="center"
        :zoom="zoom"
        style="height: 100%; width: 100%;"
    >
        <!-- User location marker -->

        <!-- Origin/Destination markers -->
        <AdvancedMarker v-if="originMarker" :options="originMarker.core_options" @dragend="locationMarkerDragged('origin', $event)">
            <template #content>
                <MapCircle />
                <TripLabel :labelType="originMarker.extra_options.id" />
            </template>
        </AdvancedMarker>
        <AdvancedMarker v-if="destMarker" :options="destMarker.core_options" @dragend="locationMarkerDragged('destination', $event)">
            <template #content>
                <MapCircle />
                <TripLabel :labelType="destMarker.extra_options.id" />
            </template>
        </AdvancedMarker>

        <!-- selected trip leg markers -->
        <template v-for="(marker, index) in tripMarkers">
            <AdvancedMarker v-if="marker.marker" :options="marker.marker.core_options">
                <template #content>
                    <MapCircle />
                </template>
            </AdvancedMarker>
            <Polyline v-if="marker.line" :options="marker.line" @click="highlightLeg(index)" />
        </template>
    </GoogleMap>
</div>
</template>

<script>
import {computed, defineComponent, nextTick, ref, watch} from "vue";
import {GoogleMap, AdvancedMarker, Polyline} from "vue3-google-map";
import {loadGoogleJavascript} from "../../utils/google";
import {useTripInput} from "./useTripInput";
import {LEG_MODE_WALK} from '../../utils/icons';
import MapCircle from "../Markers/MapCircle.vue";
import TripLabel from "../Markers/TripLabel.vue";

function buildMarkersAndLines (mapRef, legs, selectedLeg, selectedLegIsLast) {
    const markers = [];
    const highlightColor = '#C44C1C';
    const noneHighlightColor = '#502059';

    legs.forEach((leg, index) => {
        markers.push({
            marker: {
                core_options: {
                    title: leg.start_name,
                    position: {
                        lat: parseFloat(leg.start_location.lat),
                        lng: parseFloat(leg.start_location.lng),
                    },
                },
                extra_options: {},
            },
            line: {
                path: mapRef.value.api.geometry.encoding.decodePath(leg.polyline.shape),
                geodesic: true,
                strokeColor: (index === selectedLeg)
                    ? highlightColor
                    : noneHighlightColor,
                strokeWeight: 4,
                strokeOpacity: leg.mode === LEG_MODE_WALK ? 0 : 1,
                fillOpacity: leg.mode === LEG_MODE_WALK ? 0 : 0.9,
                fillColor: leg.mode === LEG_MODE_WALK ? highlightColor : null,
                icons: leg.mode === LEG_MODE_WALK ? [{
                    icon: {
                        path: 'M 0,-1 0,1',
                        strokeOpacity: 1,
                        scale: index === selectedLeg ? 4 : 3,
                    },
                    offset: '0',
                    repeat: '20px',
                }] : [],
            }
        });

        if (index === legs.length - 1) {

            markers.push({
                marker: {
                    core_options: {
                        title: leg.finish_name,
                        position: {
                            lat: parseFloat(leg.finish_location.lat),
                            lng: parseFloat(leg.finish_location.lng),
                        },
                    },
                    extra_options: {},
                },
                line: null,
            });
        }
    });

    return markers;
}

export default defineComponent({
    name: "TripPlannerMap",
    components: {
        TripLabel,
        MapCircle,
        GoogleMap,
        Polyline,
        AdvancedMarker,
    },

    props: {
        googleApiKey: {
            type: String,
            required: true,
        },
        center: {
            type: Object,
            default() {
                return {
                    lat: 33.451284,
                    lng: -112.07454,
                }
            },
            validator(value) {
                return value.hasOwnProperty('lat') && value.hasOwnProperty('lng');
            }
        },
        zoom: {
            type: Number,
            default: 10,
        },
        route: {
            type: [Object, null],
            required: false,
            default: null,
        },
        highlightedLeg: {
            type: Number,
            required: false,
            default: -1,
        },
    },

    setup(props, { emit }) {
        const mapRef = ref(null);
        const apiPromise = ref(null);

        const { googleApiKey } = props;

        const {locationFrom, locationTo, isMapVisibleOnSmallScreens} = useTripInput();

        apiPromise.value = loadGoogleJavascript(googleApiKey);

        // Generate the markers.
        const originMarker = computed(() => {
            if (! locationFrom.value.location?.lng) {
                return null;
            }

            return {
                core_options: {
                    title: 'From',
                    gmpClickable: true,
                    gmpDraggable: true,
                    position: {
                        lat: parseFloat(locationFrom.value.location.lat),
                        lng: parseFloat(locationFrom.value.location.lng),
                    },
                },
                extra_options: {
                    id: 'origin',
                },
            };
        });

        const destMarker = computed(() => {
            if (! locationTo.value.location?.lng) {
                return null;
            }

            return {
                core_options: {
                    title: 'To',
                    gmpClickable: true,
                    gmpDraggable: true,
                    position: {
                        lat: parseFloat(locationTo.value.location.lat),
                        lng: parseFloat(locationTo.value.location.lng),
                    },
                },
                extra_options: {
                    id: 'destination',
                }
            };
        });

        // Build trip markers and lines.
        const tripMarkers = ref([]);

        function updateMarkersAndLines() {
            tripMarkers.value = buildMarkersAndLines(
                mapRef,
                props.route?.legs || [],
                props.highlightedLeg,
                props.highlightedLeg === (props.route?.legs || []).length - 1
            );

            if (mapRef.value?.ready && props.route) {
                const highlightedLegRoute = (props.highlightedLeg > -1)
                    ? props.route.legs[props.highlightedLeg]
                    : null;

                if (! highlightedLegRoute) {
                    mapRef.value.map.fitBounds(
                        new mapRef.value.api.LatLngBounds(
                            props.route.summary.bounds.southwest,
                            props.route.summary.bounds.northeast
                        )
                    );
                    return;
                }

                const lats = [highlightedLegRoute.start_location.lat, highlightedLegRoute.finish_location.lat];
                const lngs = [highlightedLegRoute.start_location.lng, highlightedLegRoute.finish_location.lng];

                lats.sort((a, b) => {
                    if (a < b) return -1;
                    if (a > b) return 1;
                    return 0;
                });

                lngs.sort((a, b) => {
                    if (a < b) return -1;
                    if (a > b) return 1;
                    return 0;
                });

                mapRef.value.map.fitBounds(
                    new mapRef.value.api.LatLngBounds(
                        { lat: lats[0], lng: lngs[0] },
                        { lat: lats[1], lng: lngs[1] }
                    )
                );
            }
        }

        // Fit the map into the selected route bounds.
        watch(() => props.route, () => updateMarkersAndLines());
        watch(() => props.highlightedLeg, () => updateMarkersAndLines());

        // When map becomes visible we update markers and lines, otherwise the map is not zoomed properly.
        watch(isMapVisibleOnSmallScreens, () => nextTick(() => updateMarkersAndLines()));

        // Emit an event when the Google maps library is ready.
        watch(() => mapRef.value?.ready, () => {
            if (mapRef.value.ready) {
                emit('ready', mapRef);
            }
        });

        function locationMarkerDragged(locationType, event) {
            const newLocation = {
                value: `${event.latLng.lat()},${event.latLng.lng()}`,
                location: {
                    lat: event.latLng.lat(),
                    lng: event.latLng.lng(),
                }
            };

            emit('location-changed', {
                from: locationType === 'origin' ? newLocation : locationFrom.value,
                to: locationType === 'destination' ? newLocation : locationTo.value,
            });
        }

        function highlightLeg(index) {
            emit('highlight-leg', (props.highlightedLeg !== index) ? { index } : { index: null });
        }

        return {
            mapRef,
            originMarker,
            destMarker,
            tripMarkers,
            highlightLeg,
            locationMarkerDragged,
            apiPromise,
        }
    }
})
</script>

<style>
</style>
